import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { VendorFormData, getVendorFormSchema } from "./edit-vendor.schema";
import BaseInput from "../../../shared/components/BaseInput";
import BaseButton from "components/common/BaseButton";
import { useIntl } from "react-intl";
import {
  useNAMStates,
  useStateByAbbr,
} from "pages/admin-app/states/hooks/useNAMStates";
import BaseAsyncPaginate from "pages/admin-app/shared/components/BaseAsyncPaginate";
import { useIndustrySpecialists } from "pages/admin-app/industry-specialist/hooks/useIndustrySpecialists";
import { loadIndustrySpecialists } from "pages/admin-app/industry-specialist/services/loadIndustrySpecialists";
import { loadStates } from "pages/admin-app/states/services/loadStates";
import { loadCountries } from "pages/admin-app/country/services/loadCountries";
import { useCountries } from "pages/admin-app/country/hooks/useCountries";

interface Props {
  vendor: VendorFormData & {
    industrySpecialist?: { id: string | number; name: string };
    country?: { id: number; name: string; abbr: string };
  };
  onSubmit: (data: VendorFormData) => void;
  isSubmitting: boolean;
}

const EditVendorForm = ({ vendor, onSubmit, isSubmitting }: Props) => {
  const intl = useIntl();
  const vendorFormSchema = getVendorFormSchema(intl);

  const methods = useForm({
    resolver: zodResolver(vendorFormSchema),
    defaultValues: vendor,
  });
  const { handleSubmit, control } = methods;
  const selectedCountry = useWatch({ control, name: "countryId" });

  const {
    data: industrySpecialists,
    isLoading: isLoadingSpecialists,
    isError: isErrorSpecialists,
    error: specialistsError,
    refetch: refetchIndustrySpecialists,
    isFetching: isFetchingSpecialists,
  } = useIndustrySpecialists({ currentPage: 1 });

  const initialIndustrySpecialistDropdownValue =
    vendor.isId && vendor.industrySpecialist?.name
      ? {
          value: vendor.isId,
          label: vendor.industrySpecialist.name,
        }
      : null;

  const {
    data: states,
    isLoading: isLoadingStates,
    isError: isErrorStates,
    error: statesError,
    refetch: refetchStates,
    isFetching: isFetchingStates,
  } = useNAMStates({
    countryId: selectedCountry,
    currentPage: 1,
  });

  const { data: selectedState } = useStateByAbbr({
    countryId: selectedCountry,
    abbr: vendor?.state,
  });

  const initialStatesDropdownValue = selectedState
    ? {
        value: selectedState.abbr,
        label: `${selectedState.name} (${selectedState.abbr})`,
      }
    : null;

  const {
    data: countries,
    isLoading: isLoadingCountries,
    isError: isErrorCountries,
    error: countriesError,
    refetch: refetchCountries,
    isFetching: isFetchingCountries,
  } = useCountries({ currentPage: 1 });

  const initialCountryDropdownValue =
    vendor.country?.id && vendor.country?.name
      ? {
          value: vendor.country.id,
          label: vendor.country.name,
        }
      : null;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="inputs-container">
        <BaseInput
          name="name"
          label={intl.formatMessage({ id: "usAdmin.name" })}
          type="text"
          containerClassName="usadmin-input-name"
        />
        <BaseInput
          name="contactName"
          label={intl.formatMessage({ id: "usAdmin.contactName.vendor" })}
          type="text"
          containerClassName="usadmin-input-contactName"
        />
        <BaseInput
          name="address"
          label={intl.formatMessage({ id: "usAdmin.address" })}
          type="text"
          containerClassName="second-column"
        />
        <div className="third-row">
          <BaseInput
            name="email"
            label={intl.formatMessage({ id: "usAdmin.email.vendor" })}
            type="text"
          />
          <BaseInput
            name="phone"
            label={intl.formatMessage({ id: "usAdmin.phone" })}
            type="text"
          />
          <BaseInput
            name="zip"
            label={intl.formatMessage({ id: "usAdmin.zip" })}
            type="text"
          />
        </div>
        <div className="third-row">
          <BaseInput
            name="city"
            label={intl.formatMessage({ id: "usAdmin.city" })}
            type="text"
          />
          <BaseAsyncPaginate
            name="state"
            label={intl.formatMessage({ id: "usAdmin.state" })}
            loadOptions={loadStates(selectedCountry)}
            initialResolvedOption={initialStatesDropdownValue}
            data={states?.data || []}
            isLoading={isLoadingStates || isFetchingStates}
            isError={isErrorStates}
            error={statesError}
            refetch={refetchStates}
            optionMapper={(state) => ({
              value: state.abbr,
              label: `${state.name} (${state.abbr})`,
            })}
            placeholder={intl.formatMessage({ id: "usAdmin.selectState" })}
            additional={{ page: 1 }}
            selectedCountry={selectedCountry}
            isDisabled={!selectedCountry}
          />
          <BaseAsyncPaginate
            name="countryId"
            label={intl.formatMessage({ id: "country" })}
            loadOptions={loadCountries}
            data={countries?.data || []}
            initialResolvedOption={initialCountryDropdownValue}
            optionMapper={(c) => ({
              value: c.id,
              label: c.name,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingCountries || isFetchingCountries}
            isError={isErrorCountries}
            error={countriesError}
            refetch={refetchCountries}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectCountry",
            })}
          />
        </div>
        <div className="second-column form-group">
          <BaseAsyncPaginate
            name="isId"
            label={intl.formatMessage({ id: "usAdmin.industrySpecialist" })}
            loadOptions={loadIndustrySpecialists}
            data={industrySpecialists?.data || []}
            initialResolvedOption={initialIndustrySpecialistDropdownValue}
            optionMapper={(spec) => ({
              value: spec.id,
              label: spec.name,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingSpecialists || isFetchingSpecialists}
            isError={isErrorSpecialists}
            error={specialistsError}
            refetch={refetchIndustrySpecialists}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectIndustrySpecialist",
            })}
          />
        </div>
        <BaseButton
          text={intl.formatMessage({ id: "usAdmin.update" })}
          type="submit"
          disabled={isSubmitting}
          className="first-column usadmin-form-submit-button"
          handleSubmit={() => {}}
        />
      </form>
    </FormProvider>
  );
};

export default EditVendorForm;
